import React, { useRef, useLayoutEffect, useCallback } from "react";
import { z } from "zod";

// Melhor schema para máscara e replacement usando Zod
export const MaskSchema = z.union([
  z.string(),
  z.array(z.string().min(1)),
  z.instanceof(RegExp),
  z.function().args(z.string()).returns(z.string()),
]);
export const ReplacementSchema = z.record(
  z.string().min(1),
  z.instanceof(RegExp)
);
export const CustomInputMaskPropsSchema = z.object({
  mask: MaskSchema,
  value: z.union([z.string(), z.number(), z.null(), z.undefined()]),
  onChange: z.function().args(z.any()).returns(z.void()),
  replacement: ReplacementSchema.optional(),
});

// Tipos derivados de Zod
export type Mask = z.infer<typeof MaskSchema>;
export type Replacement = z.infer<typeof ReplacementSchema>;
export type CustomInputMaskPropsZod = z.infer<
  typeof CustomInputMaskPropsSchema
>;

/**
 * Componente de input controlado com máscara customizável.
 *
 * Permite máscaras dinâmicas (array de máscaras, ex: CPF/CNPJ) e propagação completa de props e eventos.
 * Acessibilidade: todos os atributos ARIA e eventos são repassados via {...rest}.
 *
 * @component
 * @example
 * // Máscara simples de telefone
 * <CustomInputMask
 *   mask="(99) 99999-9999"
 *   value={phone}
 *   onChange={e => setPhone(e.target.value)}
 * />
 *
 * @example
 * // Máscara dinâmica para CPF/CNPJ
 * <CustomInputMask
 *   mask={["999.999.999-99", "99.999.999/9999-99"]}
 *   value={doc}
 *   onChange={e => setDoc(e.target.value)}
 * />
 *
 * @example
 * // Máscara customizada com replacement
 * <CustomInputMask
 *   mask="AA-9999"
 *   value={placa}
 *   onChange={e => setPlaca(e.target.value)}
 *   replacement={{ "A": /[A-Z]/, "9": /\d/ }}
 *   aria-label="Placa do veículo"
 * />
 *
 * @param {Object} props
 * @param {string|string[]} props.mask - Máscara ou array de máscaras (dinâmico).
 * @param {string} props.value - Valor controlado do input.
 * @param {function} props.onChange - Callback padrão do React para mudança de valor.
 * @param {Object} [props.replacement] - Símbolos customizados para a máscara (ex: { "9": /\d/ }).
 * @param {...any} [props.rest] - Todos os outros atributos e eventos nativos de <input /> (ex: onBlur, aria-label).
 */
export interface CustomInputMaskProps
  extends Omit<
    React.InputHTMLAttributes<HTMLInputElement>,
    "onChange" | "value" | "onInvalid"
  > {
  mask: string | string[] | RegExp | ((value: string) => string);
  value: string | number | undefined | null;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  replacement?: Record<string, RegExp>;
  showInvalidFeedback?: boolean;
  customOnInvalid?: (reason: "pattern" | "regex", value: string) => void;
  clearOnInvalid?: boolean;
  invalidFeedback?: string | ((value: string) => React.ReactNode);
  smartPasteRefs?: Array<
    | React.RefObject<HTMLInputElement>
    | (() => HTMLInputElement | null)
    | HTMLInputElement
    | null
  >;
  smartPasteMasks?: Array<string | string[] | RegExp>;
  smartPasteReplacements?: Array<Record<string, RegExp>>;
  tabOnComplete?: boolean;
  onTab?: () => void;
}

/**
 * Seleciona a melhor máscara para o valor informado.
 * Se mask for array, retorna a máscara cujo número de dígitos "limpos" >= value.length, mas a menor possível.
 */
export function getBestMask(
  mask: string | string[] | RegExp | ((value: string) => string),
  value: string | number | undefined | null
): string | RegExp {
  if (typeof mask === "function") return mask(String(value ?? ""));
  if (mask instanceof RegExp) return mask;
  if (Array.isArray(mask)) {
    const cleanValue = String(value ?? "").replace(/[^a-zA-Z0-9]/g, "");
    // Ordena do menor para o maior
    const sorted = [...mask].sort(
      (a, b) =>
        a.replace(/[^a-zA-Z0-9]/g, "").length -
        b.replace(/[^a-zA-Z0-9]/g, "").length
    );
    // Escolhe a menor máscara que comporte o valor
    return (
      sorted.find(
        (m) => cleanValue.length <= m.replace(/[^a-zA-Z0-9]/g, "").length
      ) || sorted[sorted.length - 1]
    );
  }
  return mask;
}

/**
 * Aplica máscara do tipo string ou array de strings (fixa ou dinâmica).
 * Corrigido: Consome corretamente caracteres do input, respeitando ordem e pulando inválidos,
 *            mas sempre avança a máscara para fixos, e só insere prefixo fixo se houver input válido.
 */
export function applyStringMask(
  value: string | undefined | null,
  mask: string | string[] | ((value: string) => string),
  replacement: Record<string, RegExp>
): string {
  if (
    value === null ||
    value === undefined ||
    value === "" ||
    !mask ||
    (Array.isArray(mask) && mask.length === 0)
  ) {
    return value == null ? "" : String(value);
  }

  const input = String(value);

  // NÃO use stripToRawValue aqui!
  const rawInput = input;
  if (Array.isArray(mask) && mask.every((m) => typeof m === "string" && m.length === 1)) {
    mask = mask.join(""); // Trata como string no resto da função!
  }

  const maskForMaxLength =
    Array.isArray(mask) &&
    mask.every((m) => typeof m === "string" && m.length === 1)
      ? mask.join("")
      : mask;

  const maskNormalized = normalizeMask(maskForMaxLength, rawInput);
  if (!maskNormalized || maskNormalized instanceof RegExp) return input;

  let fixedPrefix = "";
  let maskIdx = 0;
  let firstEditableIdx = 0;
  while (
    maskIdx < maskNormalized.length &&
    !(replacement[maskNormalized[maskIdx]] instanceof RegExp) &&
    !["A", "a", "B", "b", "9", "*"].includes(maskNormalized[maskIdx])
  ) {
    fixedPrefix += maskNormalized[maskIdx];
    maskIdx++;
  }
  firstEditableIdx = maskIdx;

  let masked = fixedPrefix;
  maskIdx = firstEditableIdx;
  let valueIdx = 0;

  while (maskIdx < maskNormalized.length) {
    const maskChar = maskNormalized[maskIdx];
    const repl = replacement[maskChar];

    if (
      repl instanceof RegExp ||
      ["A", "a", "B", "b", "9", "*"].includes(maskChar)
    ) {
      // Procura o próximo caractere válido do input para o slot atual da máscara
      let found = false;
      while (valueIdx < rawInput.length) {
        const char = rawInput[valueIdx];
        let outChar = char;
        let isValid = false;

        switch (maskChar) {
          case "A":
            isValid = /[A-Za-z]/.test(char); // Aceita qualquer letra
            outChar = char.toUpperCase();
            break;
          case "a":
            isValid = /[A-Za-z]/.test(char); // Aceita qualquer letra
            outChar = char.toLowerCase();
            break;
          case "B":
            isValid = /[A-Za-z]/.test(char);
            outChar = char.toUpperCase();
            break;
          case "b":
            isValid = /[A-Za-z]/.test(char);
            outChar = char.toLowerCase();
            break;
          case "9":
            isValid = /\d/.test(char);
            break;
          case "*":
            isValid = /[A-Za-z0-9]/.test(char);
            break;
          default:
            if (repl instanceof RegExp) {
              isValid = repl.test(char);
            }
        }

        if (isValid) {
          masked += outChar;
          maskIdx++;
          valueIdx++;
          found = true;
          break;
        } else {
          valueIdx++;
        }
      }
      if (!found) break; // Não achou válido para o slot atual, encerra máscara
    } else {
      // slot fixo
      masked += maskChar;
      maskIdx++;
    }
  }

  // Adiciona os caracteres fixos restantes ao final da máscara
  while (maskIdx < maskNormalized.length) {
    const maskChar = maskNormalized[maskIdx];
    const repl = replacement[maskChar];
    if (
      !(repl instanceof RegExp) &&
      maskChar !== "A" &&
      maskChar !== "a" &&
      maskChar !== "B" &&
      maskChar !== "b" &&
      maskChar !== "9" &&
      maskChar !== "*"
    ) {
      masked += maskChar;
      maskIdx++;
    } else {
      break;
    }
  }

  // PATCH: Se não há editáveis preenchidos, retorna apenas o prefixo fixo (se houver)
  const editableCount = (maskNormalized.match(/[AaBb9*]/g) || []).length;
  const onlyFixed = editableCount === 0;
  if (onlyFixed) return maskNormalized;

  const prefixMatch = maskNormalized.match(/^[^AaBb9*]+/);
  const fixedPrefixFromMatch = prefixMatch ? prefixMatch[0] : "";
  const maskedEditables = (masked.match(/[A-Za-z0-9]/g) || []).length;
  if (!onlyFixed && maskedEditables === 0) return fixedPrefixFromMatch;

  if (masked.length > maskNormalized.length) {
    masked = masked.slice(0, maskNormalized.length);
  }

  return masked;
}
/**
 * Aplica máscara do tipo RegExp (apenas valida, não formata).
 */
export function applyRegExpMask(
  value: string | undefined | null,
  mask: RegExp
): string {
  if (value === null || value === undefined || value === "") return "";
  const input = String(value);
  return mask.test(input) ? input : "";
}

/**
 * Função principal de aplicação de máscara, delega para a função correta.
 */
export function applyMask(
  value: string | undefined | null,
  mask: string | string[] | RegExp | ((value: string) => string),
  replacement: Record<string, RegExp>
): string {
  const parsedMask = MaskSchema.safeParse(mask);
  if (!parsedMask.success) {
    return value == null ? "" : String(value);
  }

  if (
    value === null ||
    value === undefined ||
    value === "" ||
    !mask ||
    (Array.isArray(mask) && mask.length === 0)
  ) {
    return value == null ? "" : String(value);
  }

  if (mask instanceof RegExp) {
    return applyRegExpMask(value, mask);
  }

  return applyStringMask(
    value,
    mask as string | string[] | ((value: string) => string),
    replacement
  );
}

export function stripMask(
  val: string | undefined | null,
  mask: string | string[] | RegExp,
  replacement: Record<string, RegExp>
) {
  if (val === null || val === undefined || val === "") return "";
  const value = String(val);
  const maskToUse = getBestMask(mask, value) as string;
  if (!maskToUse) return value.replace(/[^a-zA-Z0-9]/g, "");

  let result = "";
  let valueIndex = 0;

  for (
    let maskIndex = 0;
    maskIndex < maskToUse.length && valueIndex < value.length;
    maskIndex++
  ) {
    const maskChar = maskToUse[maskIndex];
    const repl = replacement[maskChar];

    if (repl instanceof RegExp) {
      // Consome o próximo caractere do valor, só adiciona se for válido
      if (repl.test(value[valueIndex])) {
        result += value[valueIndex];
      }
      valueIndex++;
    } else {
      // Caracteres fixos da máscara: só avança na máscara, consome valor se igual ao fixo
      if (value[valueIndex] === maskChar) {
        valueIndex++;
      }
      // Não adiciona nada ao resultado
    }
  }
  return result;
}

// PATCH: CustomInputMask - robust fallback for falsy mask (no masking at all)
export const CustomInputMask = React.forwardRef<
  HTMLInputElement,
  CustomInputMaskProps
>(
  (
    {
      mask,
      value,
      onChange,
      replacement,
      showInvalidFeedback,
      customOnInvalid,
      invalidFeedback,
      smartPasteRefs,
      smartPasteMasks,
      smartPasteReplacements,
      tabOnComplete,
      onTab,
      ...inputProps
    },
    ref
  ) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const caretPosRef = useRef<number | null>(null);
    const ignoreNextChange = useRef(false);
    const [invalid, setInvalid] = React.useState(false);

    const effectiveReplacement: Record<string, RegExp> = React.useMemo(
      () => ({
        "9": /\d/,
        a: /[a-z]/,
        A: /[A-Z]/,
        b: /[A-Za-z]/,
        B: /[A-Za-z]/,
        "*": /[A-Za-z0-9]/,
        ...(replacement || {}),
      }),
      [replacement]
    );

    const setRefs = useCallback(
      (el: HTMLInputElement | null) => {
        inputRef.current = el;
        if (typeof ref === "function") {
          ref(el);
        } else if (ref) {
          (ref as React.MutableRefObject<HTMLInputElement | null>).current = el;
        }
      },
      [ref]
    );

    // PATCH: Se mask for falsy, renderiza input puro, sem mascaramento
    const isMaskFalsy = !mask || (Array.isArray(mask) && mask.length === 0);

    // Handlers SEMPRE definidos, mas só usados se input mascarado
    const handlePaste = useCallback(
      (e: React.ClipboardEvent<HTMLInputElement>) => {
        if (inputProps.readOnly || inputProps.disabled) return;
        e.preventDefault();
        const pasted = e.clipboardData.getData("Text");

        if (Array.isArray(smartPasteRefs) && smartPasteRefs.length > 1) {
          smartPasteToFields(
            pasted,
            smartPasteRefs.map((r) =>
              typeof r === "function"
                ? r()
                : r && typeof r === "object" && "current" in r
                ? (r as React.RefObject<HTMLInputElement>).current
                : (r as HTMLInputElement | null)
            ),
            Array.isArray(smartPasteMasks)
              ? (smartPasteMasks.filter(
                  (m) =>
                    typeof m === "string" ||
                    Array.isArray(m) ||
                    m instanceof RegExp
                ) as (string | string[] | RegExp)[])
              : [typeof mask === "function" ? "" : mask],
            Array.isArray(smartPasteReplacements)
              ? smartPasteReplacements
              : effectiveReplacement
          );
          return;
        }

        const input = inputRef.current;
        if (!input) return;

        const selectionStart = input.selectionStart ?? 0;
        const selectionEnd = input.selectionEnd ?? 0;
        const currentValue = String(input.value ?? "");
        const before = currentValue.slice(0, selectionStart);
        const after = currentValue.slice(selectionEnd);
        const newValue = before + pasted + after;

        let masked: string;
        if (mask instanceof RegExp) {
          masked = mask.test(newValue) ? newValue : "";
        } else if (typeof mask === "function") {
          masked = String(mask(String(newValue)));
        } else if (isMaskFalsy) {
          masked = newValue;
        } else {
          masked = applyMask(String(newValue), mask, effectiveReplacement);
        }

        ignoreNextChange.current = true;
        onChange(createSyntheticChangeEvent(input, masked));

        setTimeout(() => {
          const caretPos = before.length + pasted.length;
          input.setSelectionRange(caretPos, caretPos);
        }, 0);
      },
      [
        smartPasteRefs,
        smartPasteMasks,
        smartPasteReplacements,
        mask,
        effectiveReplacement,
        onChange,
        inputProps,
        isMaskFalsy,
      ]
    );

    const handleChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        if (inputProps.readOnly || inputProps.disabled) return;
        if (ignoreNextChange.current) {
          ignoreNextChange.current = false;
          return;
        }
        const input = e.target;
        const currentValue = String(input.value ?? "");

        let masked: string;
        if (mask instanceof RegExp) {
          masked = currentValue;
        } else if (typeof mask === "function") {
          masked = String(mask(currentValue));
        } else if (isMaskFalsy) {
          masked = currentValue;
        } else if (typeof mask === "string" || Array.isArray(mask)) {
          masked = applyMask(currentValue, mask, effectiveReplacement);
        } else {
          masked = currentValue;
        }

        caretPosRef.current = masked.length;

        const event = createSyntheticChangeEvent(input, masked);
        onChange(event);

        if (invalid) setInvalid(false);

        if (
          tabOnComplete &&
          masked.length ===
            (typeof mask === "string"
              ? (mask.match(/([a-zA-Z9*])/g) || []).length
              : Array.isArray(mask)
              ? Math.max(
                  ...mask.map((m) => (m.match(/([a-zA-Z9*])/g) || []).length)
                )
              : 0)
        ) {
          onTab?.();
        }
      },
      [
        invalid,
        mask,
        onChange,
        tabOnComplete,
        onTab,
        inputProps.readOnly,
        inputProps.disabled,
        effectiveReplacement,
        isMaskFalsy,
      ]
    );

    const handleFocus = useCallback(() => {}, []);

    const handleBlur = useCallback(
      (e: React.FocusEvent<HTMLInputElement>) => {
        if (mask instanceof RegExp) {
          const input = e.target as HTMLInputElement;
          const val = String(input.value ?? "");
          if (!mask.test(val)) {
            setInvalid(true);
            if (typeof customOnInvalid === "function") {
              customOnInvalid("regex", val);
            }
            if (inputProps.clearOnInvalid) {
              onChange(createSyntheticChangeEvent(input, ""));
            }
          } else {
            setInvalid(false);
          }
        } else if (typeof mask === "string" || Array.isArray(mask)) {
          const input = e.target as HTMLInputElement;
          const val = String(input.value ?? "");
          if (isMaskFalsy) {
            onChange(createSyntheticChangeEvent(input, val));
          } else {
            const masked = applyMask(val, mask, effectiveReplacement);
            onChange(createSyntheticChangeEvent(input, masked));
          }
        } else if (inputProps.pattern) {
          const input = e.target as HTMLInputElement;
          const val = String(input.value ?? "");
          const pattern = new RegExp(`^${inputProps.pattern}$`);
          if (!pattern.test(val)) {
            setInvalid(true);
            if (typeof customOnInvalid === "function") {
              customOnInvalid("pattern", val);
            }
          } else {
            setInvalid(false);
          }
        }
        if (inputProps.onBlur) {
          inputProps.onBlur(e);
        }
      },
      [customOnInvalid, mask, onChange, inputProps, effectiveReplacement, isMaskFalsy]
    );

    useLayoutEffect(() => {
      if (inputProps.autoFocus && inputRef.current) {
        inputRef.current.focus();
        if (!value) inputRef.current.setSelectionRange(0, 0);
      }
    }, [inputProps.autoFocus, value]);

    useLayoutEffect(() => {
      if (mask instanceof RegExp) return;
      const input = inputRef.current;
      if (input) {
        setTimeout(() => {
          input.setSelectionRange(input.value.length, input.value.length);
        }, 0);
      }
    }, [value, mask, effectiveReplacement]);

    let maskForMaxLength: string | undefined;
    if (typeof mask === "string") {
      maskForMaxLength = mask;
    } else if (Array.isArray(mask) && mask.length > 0) {
      if (mask.every((m) => typeof m === "string" && m.length === 1)) {
        maskForMaxLength = mask.join("");
      } else {
        maskForMaxLength = mask[0];
      }
    } else {
      maskForMaxLength = undefined;
    }

    // PATCH: input puro se mask for falsy
    if (isMaskFalsy) {
      return (
        <input
          {...inputProps}
          ref={setRefs}
          value={value == null ? "" : String(value)}
          onChange={e => {
            if (inputProps.readOnly || inputProps.disabled) return;
            onChange(e);
          }}
          onPaste={inputProps.onPaste}
          onBlur={e => {
            if (inputProps.readOnly || inputProps.disabled) return;
            if (inputProps.onBlur) inputProps.onBlur(e);
            const input = e.target as HTMLInputElement;
            onChange(createSyntheticChangeEvent(input, input.value));
          }}
          onFocus={inputProps.onFocus}
          autoComplete={inputProps.autoComplete ?? undefined}
          aria-label={inputProps["aria-label"]}
          aria-invalid={inputProps["aria-invalid"]}
          aria-describedby={inputProps["aria-describedby"]}
          role="textbox"
          style={inputProps.style}
          disabled={inputProps.disabled}
          readOnly={inputProps.readOnly}
          placeholder={inputProps.placeholder}
          maxLength={inputProps.maxLength}
          inputMode={inputProps.inputMode}
          pattern={inputProps.pattern}
        />
      );
    }

    // Valor mascarado para input mascarado
    let maskedValue: string;
    if (value === null || value === undefined) {
      maskedValue = "";
    } else if (mask instanceof RegExp) {
      maskedValue = String(value ?? "");
    } else if (typeof mask === "string" || Array.isArray(mask)) {
      maskedValue = applyMask(
        String(value ?? ""),
        mask,
        effectiveReplacement
      );
    } else {
      maskedValue = String(
        typeof mask === "function" ? mask(String(value)) : value
      );
    }

    return (
      <div style={{ display: "inline-block" }}>
        <input
          {...inputProps}
          ref={setRefs}
          value={maskedValue}
          onChange={handleChange}
          onPaste={handlePaste}
          onBlur={handleBlur}
          onFocus={handleFocus}
          pattern={
            inputProps.pattern ??
            (mask instanceof RegExp ? mask.source : undefined)
          }
          maxLength={
            inputProps.maxLength ??
            (typeof maskForMaxLength === "string"
              ? maskForMaxLength.length
              : undefined)
          }
          placeholder={
            inputProps.placeholder ??
            (typeof maskForMaxLength === "string"
              ? maskForMaxLength.replace(/[a-zA-Z9*]/g, "_")
              : undefined)
          }
          inputMode={
            inputProps.inputMode ??
            (typeof maskForMaxLength === "string"
              ? maskForMaxLength.includes("9")
                ? "numeric"
                : "text"
              : "text")
          }
          aria-invalid={
            invalid || inputProps["aria-invalid"] ? true : undefined
          }
          aria-describedby={
            showInvalidFeedback && invalid
              ? "inputmask-error"
              : inputProps["aria-describedby"]
          }
          role="textbox"
          style={{
            ...(inputProps.style || {}),
            borderColor: invalid ? "#dc3545" : inputProps.style?.borderColor,
            outlineColor: invalid ? "#dc3545" : inputProps.style?.outlineColor,
            backgroundColor: inputProps.disabled
              ? "#e9ecef"
              : inputProps.readOnly
              ? "#f8f9fa"
              : inputProps.style?.backgroundColor,
          }}
          disabled={inputProps.disabled}
          readOnly={inputProps.readOnly}
        />
        {showInvalidFeedback && invalid && (
          <div
            id="inputmask-error"
            style={{
              color: "#dc3545",
              fontSize: "0.85em",
              marginTop: 2,
              minHeight: 18,
            }}
            role="alert"
            aria-live="polite"
          >
            {typeof invalidFeedback === "function"
              ? invalidFeedback(String(value ?? ""))
              : invalidFeedback ?? "Valor inválido para o formato esperado."}
          </div>
        )}
      </div>
    );
  }
);
CustomInputMask.displayName = "CustomInputMask";

/**
 * Cria um evento sintético de onChange para testes e React.
 */
function createSyntheticChangeEvent(
  input: HTMLInputElement,
  value: string
): React.ChangeEvent<HTMLInputElement> {
  return {
    ...({} as React.ChangeEvent<HTMLInputElement>),
    target: {
      ...input,
      value,
    },
    currentTarget: {
      ...input,
      value,
    },
    bubbles: true,
    cancelable: true,
    defaultPrevented: false,
    isDefaultPrevented: () => false,
    isPropagationStopped: () => false,
    preventDefault: () => {},
    stopPropagation: () => {},
    nativeEvent: {} as Event,
    persist: () => {},
    type: "change",
  };
}

// Função utilitária para normalizar a máscara (string, array, função, RegExp)
function normalizeMask(
  mask: string | string[] | RegExp | ((value: string) => string),
  value: string | number | undefined | null
): string | RegExp {
  if (typeof mask === "function") return mask(String(value ?? ""));
  if (mask instanceof RegExp) return mask;
  if (Array.isArray(mask)) {
    // PATCH: Se for array de caracteres, trata como string única
    if (mask.every((m) => typeof m === "string" && m.length === 1)) {
      return mask.join("");
    }
    // Caso contrário, lógica dinâmica (array de máscaras)
    const cleanValue = String(value ?? "").replace(/[^a-zA-Z0-9]/g, "");
    const sorted = [...mask].sort(
      (a, b) =>
        a.replace(/[^a-zA-Z0-9]/g, "").length -
        b.replace(/[^a-zA-Z0-9]/g, "").length
    );
    return (
      sorted.find(
        (m) => cleanValue.length <= m.replace(/[^a-zA-Z0-9]/g, "").length
      ) || sorted[sorted.length - 1]
    );
  }
  return mask;
}

/**
 * Utilitário para espalhar caracteres colados em múltiplos campos sequenciais.
 * Exemplo de uso: inputs de data (DD/MM/AAAA) ou código de verificação (XXXXXX).
 *
 * @param pasted Valor colado (string)
 * @param fields Array de refs dos inputs na ordem desejada
 * @param masks  Array de máscaras (string ou RegExp) na mesma ordem dos campos
 * @param replacements Array de replacements (ou um só para todos)
 */
export function smartPasteToFields(
  pasted: string,
  fields: Array<HTMLInputElement | null>,
  masks: Array<string | string[] | RegExp>,
  replacements: Array<Record<string, RegExp>> | Record<string, RegExp>
) {
  let cursor = 0;
  for (let i = 0; i < fields.length; i++) {
    const input = fields[i];
    if (!input) continue;
    const mask = masks[i];
    // Sempre mistura os defaults:
    const baseReplacement = {
      "9": /\d/,
      a: /[a-z]/, // <-- Corrigido: só aceita minúsculas
      "*": /[A-Za-z0-9]/,
    };
    const customReplacement = Array.isArray(replacements)
      ? replacements[i]
      : replacements;
    // Garante que replacement nunca seja vazio e nunca tenha valores undefined
    const replacement: Record<string, RegExp> = Object.fromEntries(
      Object.entries({
        ...baseReplacement,
        ...(customReplacement || {}),
      }).filter(([, v]) => v instanceof RegExp)
    );

    // Calcula quantos caracteres cabem neste campo
    const maskStr =
      typeof mask === "string" ? mask : Array.isArray(mask) ? mask[0] : "";
    const maxLen = maskStr ? maskStr.length : 99;
    const slice = pasted.slice(cursor, cursor + maxLen);
    let masked: string;
    if (mask instanceof RegExp) {
      // For RegExp, just check if the slice matches, otherwise set empty
      masked = mask.test(slice) ? slice : "";
    } else {
      masked = applyMask(slice, mask, replacement);
    }
    input.value = masked;
    // Dispara evento para React atualizar o estado
    const event = new Event("input", { bubbles: true });
    input.dispatchEvent(event);
    cursor += slice.length;
    if (cursor >= pasted.length) break;
  }
}

